/**
 * Global dictionary management page.
 * Allows tenants to define words that should always be redacted or always allowed
 * across all cases (global scope).
 */

import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Plus, Pencil, Trash2, Check, Loader2, BookOpen } from 'lucide-react';
import { Button } from '../components/ui/Button';
import { useDictionary } from '../hooks/useDictionary';
import type { WordDictionaryEntry } from '../types';

type ActiveTab = 'always_allow' | 'always_redact';

export function DictionaryPage() {
  const navigate = useNavigate();
  const { entries, isLoading, error, addEntry, updateEntry, deleteEntry } = useDictionary('global');

  const [activeTab, setActiveTab] = useState<ActiveTab>('always_redact');

  // Add form state
  const [newWord, setNewWord] = useState('');
  const [newMatchType, setNewMatchType] = useState<'exact' | 'contains'>('exact');
  const [newReason, setNewReason] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Edit state
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editingWord, setEditingWord] = useState('');
  const [editingMatchType, setEditingMatchType] = useState<'exact' | 'contains'>('exact');
  const [editingReason, setEditingReason] = useState('');

  const filteredEntries = entries.filter((e) => e.action === activeTab);

  const handleAdd = async () => {
    if (!newWord.trim()) return;
    setIsSubmitting(true);
    const result = await addEntry({
      word: newWord.trim(),
      action: activeTab,
      matchType: newMatchType,
      reason: activeTab === 'always_redact' ? newReason.trim() || undefined : undefined,
    });
    setIsSubmitting(false);
    if (result) {
      setNewWord('');
      setNewReason('');
      setNewMatchType('exact');
    }
  };

  const handleStartEdit = (entry: WordDictionaryEntry) => {
    setEditingId(entry.id);
    setEditingWord(entry.word);
    setEditingMatchType(entry.match_type);
    setEditingReason(entry.reason || '');
  };

  const handleCancelEdit = () => {
    setEditingId(null);
    setEditingWord('');
    setEditingReason('');
    setEditingMatchType('exact');
  };

  const handleSaveEdit = async () => {
    if (!editingId || !editingWord.trim()) return;
    setIsSubmitting(true);
    const result = await updateEntry(editingId, {
      word: editingWord.trim(),
      matchType: editingMatchType,
      reason: activeTab === 'always_redact' ? editingReason.trim() || undefined : undefined,
    });
    setIsSubmitting(false);
    if (result) {
      handleCancelEdit();
    }
  };

  const handleDelete = async (id: string) => {
    if (!window.confirm('Delete this dictionary entry?')) return;
    await deleteEntry(id);
  };

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="border-b border-border bg-white">
        <div className="max-w-4xl mx-auto px-6 py-4 flex items-center gap-4">
          <button
            onClick={() => navigate('/')}
            className="p-2 hover:bg-muted rounded-lg transition-colors"
            aria-label="Back"
          >
            <ArrowLeft className="w-5 h-5 text-foreground" />
          </button>
          <div className="flex items-center gap-2">
            <BookOpen className="w-5 h-5 text-[#7035C4]" />
            <h1 className="text-lg font-semibold">Global Word Dictionary</h1>
          </div>
          <p className="text-sm text-muted-foreground ml-2">
            Words defined here apply across all cases (tenant-wide).
          </p>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-6 py-8">
        {/* Tabs */}
        <div className="flex gap-2 mb-6 border-b border-border">
          <button
            onClick={() => setActiveTab('always_redact')}
            className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
              activeTab === 'always_redact'
                ? 'border-[#7035C4] text-[#7035C4]'
                : 'border-transparent text-muted-foreground hover:text-foreground'
            }`}
          >
            Always Redact
          </button>
          <button
            onClick={() => setActiveTab('always_allow')}
            className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
              activeTab === 'always_allow'
                ? 'border-[#7035C4] text-[#7035C4]'
                : 'border-transparent text-muted-foreground hover:text-foreground'
            }`}
          >
            Allow List
          </button>
        </div>

        {/* Add entry form */}
        <div className="bg-white border border-border rounded-lg p-4 mb-6">
          <h3 className="text-sm font-medium mb-3">
            Add to {activeTab === 'always_redact' ? 'Always Redact' : 'Allow List'}
          </h3>
          <div className="flex gap-2 items-start flex-wrap">
            <input
              type="text"
              value={newWord}
              onChange={(e) => setNewWord(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && handleAdd()}
              placeholder="Word or phrase"
              className="border border-border rounded-md px-3 py-2 text-sm flex-1 min-w-[180px] focus:outline-none focus:ring-2 focus:ring-[#7035C4]/30"
            />
            <select
              value={newMatchType}
              onChange={(e) => setNewMatchType(e.target.value as 'exact' | 'contains')}
              className="border border-border rounded-md px-3 py-2 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-[#7035C4]/30"
            >
              <option value="exact">Exact match</option>
              <option value="contains">Contains</option>
            </select>
            {activeTab === 'always_redact' && (
              <input
                type="text"
                value={newReason}
                onChange={(e) => setNewReason(e.target.value)}
                placeholder="Reason (optional)"
                className="border border-border rounded-md px-3 py-2 text-sm flex-1 min-w-[180px] focus:outline-none focus:ring-2 focus:ring-[#7035C4]/30"
              />
            )}
            <Button
              onClick={handleAdd}
              disabled={!newWord.trim() || isSubmitting}
              className="bg-[#7035C4] hover:bg-[#5c2a9e] text-white shrink-0"
            >
              {isSubmitting ? <Loader2 className="h-4 w-4 animate-spin" /> : <Plus className="h-4 w-4 mr-1" />}
              Add
            </Button>
          </div>
        </div>

        {/* Entries list */}
        {isLoading ? (
          <div className="flex justify-center py-12">
            <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
          </div>
        ) : error ? (
          <p className="text-destructive text-sm">{error}</p>
        ) : filteredEntries.length === 0 ? (
          <div className="text-center py-12 text-muted-foreground text-sm">
            No entries yet. Add a word or phrase above.
          </div>
        ) : (
          <div className="bg-white border border-border rounded-lg overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-muted/50">
                <tr>
                  <th className="text-left px-4 py-3 font-medium text-muted-foreground">Word / Phrase</th>
                  <th className="text-left px-4 py-3 font-medium text-muted-foreground">Match</th>
                  {activeTab === 'always_redact' && (
                    <th className="text-left px-4 py-3 font-medium text-muted-foreground">Reason</th>
                  )}
                  <th className="px-4 py-3 w-20" />
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {filteredEntries.map((entry) => (
                  <tr key={entry.id} className="hover:bg-muted/20">
                    {editingId === entry.id ? (
                      <>
                        <td className="px-4 py-2">
                          <input
                            type="text"
                            value={editingWord}
                            onChange={(e) => setEditingWord(e.target.value)}
                            className="border border-border rounded-md px-2 py-1 text-sm w-full focus:outline-none focus:ring-2 focus:ring-[#7035C4]/30"
                          />
                        </td>
                        <td className="px-4 py-2">
                          <select
                            value={editingMatchType}
                            onChange={(e) => setEditingMatchType(e.target.value as 'exact' | 'contains')}
                            className="border border-border rounded-md px-2 py-1 text-sm bg-white focus:outline-none"
                          >
                            <option value="exact">Exact</option>
                            <option value="contains">Contains</option>
                          </select>
                        </td>
                        {activeTab === 'always_redact' && (
                          <td className="px-4 py-2">
                            <input
                              type="text"
                              value={editingReason}
                              onChange={(e) => setEditingReason(e.target.value)}
                              placeholder="Reason"
                              className="border border-border rounded-md px-2 py-1 text-sm w-full focus:outline-none focus:ring-2 focus:ring-[#7035C4]/30"
                            />
                          </td>
                        )}
                        <td className="px-4 py-2">
                          <div className="flex items-center gap-1">
                            <button
                              onClick={handleSaveEdit}
                              disabled={isSubmitting}
                              className="p-1 hover:bg-green-100 text-green-600 rounded"
                            >
                              <Check className="h-4 w-4" />
                            </button>
                            <button
                              onClick={handleCancelEdit}
                              className="p-1 hover:bg-gray-100 text-gray-500 rounded"
                            >
                              ✕
                            </button>
                          </div>
                        </td>
                      </>
                    ) : (
                      <>
                        <td className="px-4 py-3 font-mono">{entry.word}</td>
                        <td className="px-4 py-3 text-muted-foreground">
                          {entry.match_type === 'exact' ? 'Exact' : 'Contains'}
                        </td>
                        {activeTab === 'always_redact' && (
                          <td className="px-4 py-3 text-muted-foreground">{entry.reason || '—'}</td>
                        )}
                        <td className="px-4 py-3">
                          <div className="flex items-center gap-1">
                            <button
                              onClick={() => handleStartEdit(entry)}
                              className="p-1 hover:bg-gray-100 text-gray-500 rounded"
                            >
                              <Pencil className="h-3.5 w-3.5" />
                            </button>
                            <button
                              onClick={() => handleDelete(entry.id)}
                              className="p-1 hover:bg-red-100 text-red-500 rounded"
                            >
                              <Trash2 className="h-3.5 w-3.5" />
                            </button>
                          </div>
                        </td>
                      </>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </main>
    </div>
  );
}
